import { Night } from "@common/definitions/night";

/*
 * The night's light (1.05), the parts without Pixi (tests/src/night105.test.ts): how bright a beam, a glow and a tracer
 * are at each point of their pictures, and the switch of your own flashlight as the page shows it before the server
 * says (see managers/nightLight.ts).
 */

/**
 * the dark over the map where no light falls: its colour and how much of the world it hides (1.05.2, the owner, 28
 * October 2026, of 1.05.1's, all but black at 0.9: "nightime looks way too dark. It should be like the attic colored
 * or less, not peach black": 0.6, the map dim but plain to see); 0.98.1, a little darker (the owner, 30 October 2026:
 * "night mode isnt that dark, should be abit darker so the flashlights can also be useful"): 0.75; 0.98.4, darker
 * still and creepier (the owner, 31 October 2026: "Night mode, lower the rightness to 87% (attic is 92% or 85% i
 * think), and make it creepier"): 0.87, the map just to be made out, still lighter than the attic's dark (0.93), the
 * blue gone colder and greener (a dead, sunken dark rather than a moonlit one), the ground in a beam many times as
 * bright as round it; with it the mist that creeps over the ground and the night's sounds (nightLight.ts, warAudio.ts)
 */
export const DARK_COLOR = 0x060c14;
export const DARK_ALPHA = 0.87;

/**
 * how much of the dark is taken off a player's body (0.98.1: `Night.body`, in place of 1.05.1's glow round them): they
 * show in the dark, a little dimmed, and the ground round them stays as dark as anywhere
 */
export const BODY_LIFT = 0.8;

/** a street light's lamp at night (0.98.1): how much of the dark its light takes off, and its warm, sodium tint over it */
export const LAMP_LIGHT = 0.9;
export const LAMP_WARM = 0.2;
export const LAMP_TINT = 0xffbd6b;

/** the light map is drawn at this share of the screen's pixels each way, and stretched over it (the lights are soft) */
export const MAP_SCALE = 0.25;

/** the camera container's zIndex for the dark: over every floor's things (0 to 4), under the gas (1000), the damage numbers and the pings */
export const NIGHT_ZINDEX = 900;

/** a beam's picture: this many pixels from its player to its far end, and as wide as its angle needs */
export const BEAM_LENGTH = 512;
export const BEAM_WIDTH = 2 * Math.ceil(BEAM_LENGTH * Math.tan(Night.halfAngle * 1.06)) + 8;
/** the glow's and the tracer's pictures */
export const GLOW_SIZE = 128;
export const STREAK_LENGTH = 64;
export const STREAK_WIDTH = 16;

const smoothstep = (edge0: number, edge1: number, x: number): number => {
    const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
    return t * t * (3 - 2 * t);
};

/**
 * How much of the dark a beam takes away at a point `distance` (a share of its reach) along it and `angle` (radians)
 * off its middle: nearly all of it from a step in front of its player to half its reach, fading out to nothing at its
 * end and its edges (never further than `Night.reach` and `Night.halfAngle`: the server counts that much and more)
 */
export function beamAlpha(distance: number, angle: number): number {
    if (distance <= 0 || distance >= 1) return 0;
    const along = (1 - smoothstep(0.42, 1, distance)) * (0.7 + 0.3 * smoothstep(0, 0.06, distance));
    const across = 1 - smoothstep(0.5, 1, Math.abs(angle) / Night.halfAngle);
    return Math.max(0, Math.min(1, along * across)) * 0.97;
}

/** a round glow, `distance` a share of its radius: bright in the middle, soft to nothing at its edge */
export function glowAlpha(distance: number): number {
    if (distance >= 1) return 0;
    return 1 - smoothstep(0.3, 1, Math.max(0, distance));
}

/**
 * a player's body lit (0.98.1), `distance` a share of `Night.body`: the whole of it, to its edge, and nothing past it
 * but the blur of the light map's pixels
 */
export function bodyAlpha(distance: number): number {
    if (distance >= 1) return 0;
    return 1 - smoothstep(0.84, 1, Math.max(0, distance));
}

/** a tracer's streak, `along` from its tail (0) to its head (1), `across` from its middle (0) to its side (1) */
export function streakAlpha(along: number, across: number): number {
    if (along <= 0 || along > 1 || Math.abs(across) >= 1) return 0;
    return along ** 1.5 * (1 - smoothstep(0.2, 1, Math.abs(across)));
}

/**
 * Your own flashlight as the page shows it: switched at once when you press the button, while the server's word comes
 * back (a round trip), then as the server says. Kept switched until the server agrees, or `PENDING_MS` have passed
 * (the server didn't take it: too soon after the last switch, say), so it never flickers back and forth on the way.
 */
export const PENDING_MS = 700;

export class OwnFlashlight {
    private _pending?: { readonly on: boolean, readonly until: number };

    /** the button was pressed: what the light shows now (the opposite of what it showed) */
    press(shown: boolean, now: number): boolean {
        this._pending = { on: !shown, until: now + PENDING_MS };
        return !shown;
    }

    /** what to show, the server saying `server` */
    shown(server: boolean, now: number): boolean {
        const pending = this._pending;
        if (!pending) return server;
        if (pending.on === server || now >= pending.until) {
            this._pending = undefined;
            return server;
        }
        return pending.on;
    }

    reset(): void { this._pending = undefined; }
}
